import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { config } from '@/config';
import { useAuth } from '@/context/AuthContext';
import { PRYSM_APP_CENTRAL_ID } from '@/constants/prysmApp';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = config.BACKEND_URL;
const BLOCKED = ['/login', '/maintenance'];

function sessionKey(versionId) {
  return `prysm_version_popup_closed:${versionId}`;
}

/**
 * Popup nouveautés Prysm'App — à la connexion, uniquement pour cette appli.
 */
export default function VersionWhatsNewModal() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [hideForever, setHideForever] = useState(false);
  const [welcomeBlocking, setWelcomeBlocking] = useState(false);

  useEffect(() => {
    const sync = () => {
      setWelcomeBlocking(document.body.getAttribute('data-welcome-open') === 'true');
    };
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-welcome-open'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!user?.id || BLOCKED.includes(pathname)) {
      setOpen(false);
      setItem(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/app-versions/pending`, {
          params: { application_id: PRYSM_APP_CENTRAL_ID },
        });
        if (cancelled) return;
        if (!res.data?.show || !res.data?.item) return;
        const versionId = res.data.item.id;
        try {
          if (sessionStorage.getItem(sessionKey(versionId))) return;
        } catch {
          /* ignore */
        }
        setItem(res.data.item);
        setHideForever(false);
      } catch {
        if (!cancelled) setItem(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, pathname]);

  useEffect(() => {
    setOpen(Boolean(user && item && !welcomeBlocking && !BLOCKED.includes(pathname)));
  }, [user, item, welcomeBlocking, pathname]);

  const close = async () => {
    if (item?.id) {
      try {
        sessionStorage.setItem(sessionKey(item.id), '1');
      } catch {
        /* ignore */
      }
      if (hideForever) {
        try {
          await axios.post(`${API}/app-versions/${item.id}/dismiss`, { hide_forever: true });
        } catch {
          /* ignore */
        }
      }
    }
    setOpen(false);
    setItem(null);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="dark:bg-gray-800 dark:border-gray-700 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="dark:text-white">
            Nouveautés Prysm&apos;App{item.version ? ` — v${item.version}` : ''}
          </DialogTitle>
          <DialogDescription>
            Corrections et améliorations de cette version.
          </DialogDescription>
        </DialogHeader>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm text-gray-800 dark:bg-gray-900 dark:text-gray-200">
          {item.changelog || 'Aucun détail.'}
        </pre>
        <div className="flex items-center gap-2">
          <Checkbox
            id="hide-version-popup"
            checked={hideForever}
            onCheckedChange={(v) => setHideForever(!!v)}
          />
          <Label htmlFor="hide-version-popup" className="text-sm font-normal dark:text-gray-200">
            Ne plus afficher
          </Label>
        </div>
        <DialogFooter>
          <Button type="button" onClick={close}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
